import { Injectable } from '@angular/core';
import { CommodityViewModel } from '@models/commodities.class';
import { indexOf, remove } from 'lodash';
import { ConnectableObservable, Observable, Observer } from 'rxjs';
import { publish, startWith, tap } from 'rxjs/operators';
import { SearchService } from 'src/core';
import { ChangeCharacterFromIssue } from 'src/shared/utility/change-character';
import { MarketType } from 'src/solace/constants/marketType.enum';
import { MarketItem } from 'src/solace/models/market/marketItem.class';
import { CommoditiesFactory } from './models/solace/solaceCommoditiesFactory.class';
import {
  BaseCommodityAdapter,
  CommodityGroups,
} from './models/solace/solaceCommoditiesType.class';

@Injectable()
export class SharedSolaceStreamService {
  public isSearchServiceUpdaterCreated: boolean;

  markets: any = {
    marketBMV: [],
    marketSIC: [],
    marketUSA: [],
    indexes: [],
    currencies: [],
    commodities: [],
  };

  marketMaps: any = {
    marketBMV: new Map<string, any>(),
    marketSIC: new Map<string, any>(),
    marketUSA: new Map<string, any>(),
    indexes: new Map<string, any>(),
    currencies: new Map<string, any>(),
    commodities: new Map<string, any>(),
  };

  public marketObservables: any = {
    marketBMV: new Map<string, any>(),
    marketSIC: new Map<string, any>(),
    marketUSA: new Map<string, any>(),
    indexes: new Map<string, any>(),
    currencies: new Map<string, any>(),
    commodities: new Map<string, any>(),
  };

  private marketObservablesConsumers: any = {
    marketBMV: new Map<string, any>(),
    marketSIC: new Map<string, any>(),
    marketUSA: new Map<string, any>(),
    indexes: new Map<string, any>(),
    currencies: new Map<string, any>(),
    commodities: new Map<string, any>(),
  };

  private commoditiesFactory: CommoditiesFactory;

  // We use any due to a possible mis-typing in createObservableSymbol
  // Check the comments on createSearchServiceUpdater
  private updateSearchIssues: (symbol: any) => void;

  constructor() {
    this.commoditiesFactory = new CommoditiesFactory();
  }

  processCallbackStream(data: any): void {
    let result = 'AGG';
    const topic: string = data.getTopic();
    const message: any = data.getBodyJson();
    if (topic.indexOf('AGG') < 0) {
      result = this.parseMessageType(message);
    }

    const marketType: any =
      topic.indexOf('NAC') > 0 ? MarketType.BMV : MarketType.SIC;
    switch (result) {
      case 'AGG':
        this.parseAggData(marketType, message);
        break;

      case 'P': // NAC
      case 'DP': // SIC
        this.parseHechos(marketType, message);
        break;

      case 'O1': // NAC
      case 'D2': // SIC
        this.parsePosturas(marketType, message, topic);
        break;

      case 'Indices2':
        if (message.GBL_INDEX !== undefined) {
          this.parseIndexes(message);
        } else if (message.AME_INDEX !== undefined) {
          this.parseIndexes(message);
        } else if (message.METALS !== undefined) {
          this.parseCommodities(message);
        } else if (message.AGRIC !== undefined) {
          this.parseCommodities(message);
        } else if (message.ENERGY !== undefined) {
          this.parseCommodities(message);
        } else if (message.CURRENCY !== undefined) {
          this.parseCurrencies(message);
        }
        break;

      default:
        console.warn(`Unknown message type received from solace: ${result}`);
        break;
    }
  }

  getObservableSymbol<T>(
    marketName: string,
    symbol: string,
    consumer: string,
  ): Observable<T> {
    return this.createObservableSymbol(symbol, marketName, consumer).pipe(
      tap(
        (symbolShadowed: T) =>
          // This will try to reflect the emitted change into the searchService
          this.updateSearchIssues && this.updateSearchIssues(symbolShadowed),
      ),
    );
  }

  removeConsumerFromObservable(
    symbol: string,
    marketName: string,
    consumer: string,
  ): void {
    const marketSymbolConsumers: any[] =
      this.marketObservablesConsumers[marketName].get(symbol);

    if (marketSymbolConsumers) {
      remove(marketSymbolConsumers, (item: string) => {
        return item === consumer;
      });

      if (marketSymbolConsumers.length === 0) {
        this.marketObservablesConsumers[marketName].delete(symbol);
        this.marketObservables[marketName].delete(symbol);
      }
    }
  }

  getConsumerFromObservable(
    symbol: string,
    marketName: string,
    consumer: string,
  ): any {
    const marketSymbolConsumers: any[] =
      this.marketObservablesConsumers[marketName].get(symbol);
    return marketSymbolConsumers;
  }

  setCommoditiesByType(
    type: CommodityGroups,
    commodities: CommodityViewModel[],
  ): void {
    for (const commodity of commodities) {
      const newCommodity = this.commoditiesFactory.makeCommodity(
        type,
        commodity,
      );
      newCommodity && this.setMessageInSubtopic(newCommodity);
    }
  }

  getMarketElement(marketName: string, issue: string): any {
    issue =
      marketName === 'indexes'
        ? issue
        : new ChangeCharacterFromIssue().ChangeAmpersand(issue);
    return this.markets[marketName][this.marketMaps[marketName].get(issue) - 1];
  }

  /**
   * Uses an instance of SearchService to update a given issue in the issues array
   *
   * We cannot inject SearchService into this service due to a possible
   * redundancy in service injection, because it's inside SharedSolaceModule that
   * we push items into the the SearchService.issues.
   *
   * We do this so everytime an issue gets an update via solace, it gets reflected
   * in the searchService, so wherever searchService is used, it'll be up to date.
   * @param searchService that should be passed from the most "general" component/service
   */
  public createSearchServiceUpdater(searchService: SearchService) {
    // We use any due to a possible mis-typing in createObservableSymbol
    this.updateSearchIssues = (symbol: any) => {
      const symbolAsMarketItem = symbol as MarketItem;

      if (
        symbolAsMarketItem &&
        symbolAsMarketItem.hechos &&
        symbolAsMarketItem.hechos.body
      ) {
        // TODO: Double check if 'existingIssue' is still necessary
        let existingIssue = searchService.issues.find(
          (issue) => issue.issueID === symbolAsMarketItem.symbol,
        );
        if (existingIssue) {
          existingIssue = {
            ...existingIssue,
            weightedAveragePrice:
              symbol.hechos.body.bmvWeightedAvgPrice > 0
                ? symbol.hechos.body.bmvWeightedAvgPrice
                : symbol.hechos.body.weightedAveragePrice,
          };
        }
      }
    };

    this.isSearchServiceUpdaterCreated = true;
  }

  private emitSymbolChange(
    symbol: string,
    marketName: string,
    newSymbol: any,
  ): void {
    symbol = new ChangeCharacterFromIssue().ChangeAmpersand(symbol);
    if (this.marketObservables[marketName].has(symbol)) {
      this.marketObservables[marketName].get(symbol).observer.next(newSymbol);
    }
  }

  private createObservableSymbol(
    symbol: string,
    marketName: string,
    consumer: string,
  ): Observable<any> {
    if (this.marketObservables[marketName].has(symbol)) {
      const marketSymbolConsumers: any[] =
        this.marketObservablesConsumers[marketName].get(symbol);

      if (indexOf(marketSymbolConsumers, consumer) === -1) {
        marketSymbolConsumers.push(consumer);
        this.marketObservablesConsumers[marketName].set(
          symbol,
          marketSymbolConsumers,
        );
      }
    } else {
      let symbolObserver: Observer<any>;

      const symbolStream$ = ConnectableObservable.create(
        (observer: any) => (symbolObserver = observer),
      ).pipe(publish());

      symbolStream$.connect();

      const observableInfo: any = {
        observable: symbolStream$,
        observer: symbolObserver,
      };

      this.marketObservables[marketName].set(symbol, observableInfo);
      this.marketObservablesConsumers[marketName].set(symbol, [consumer]);
    }

    const lastSymbol: any = this.getMarketElement(marketName, symbol);
    return this.marketObservables[marketName]
      .get(symbol)
      .observable.pipe(startWith(lastSymbol));
  }

  private parseAggData(marketType: MarketType, message: any): void {
    const marketName: string =
      marketType === MarketType.BMV ? 'marketBMV' : 'marketSIC';

    const symbol: MarketItem = this.getMarketElement(
      marketName,
      message.StockSeries,
    );

    if (symbol) {
      // console.log('message: %o', message);
      Object.assign(symbol.hechos.body, {
        aggVolume: message.AggregateVolume || 0,
        variationPercentage: message.PercentageChange || 0,
        maxPrice: message.MaxPrice || 0,
        minPrice: message.MinPrice || 0,
        openPrice: message.OpenPrice || 0,
        bmvWeightedAvgPrice: message.BMVWeightedAvgPrice,
        ppp: message.BMVWeightedAvgPrice
          ? message.BMVWeightedAvgPrice === 0
            ? message.WeightedAvgPrice
            : message.BMVWeightedAvgPrice
          : message.WeightedAvgPrice,
      });
      this.emitSymbolChange(message.StockSeries, marketName, symbol);
    }
  }

  private parseHechos(marketType: MarketType, message: any): void {
    const marketName: string =
      marketType === MarketType.BMV ? 'marketBMV' : 'marketSIC';
    const symbol: MarketItem = this.getMarketElement(
      marketName,
      message.StockSeries,
    );
    if (symbol) {
      const messageClone: any = Object.assign({}, message);

      messageClone.lastWithLot = parseFloat(message.Last);

      if (message.Odd_lot !== 'P') {
        messageClone.lastPrice = parseFloat(message.Last);
      } else {
        messageClone.lastPrice = symbol.hechos.body.lastPrice;
      }

      messageClone.operationVolume = parseFloat(message.OperationVolume) || 0;
      messageClone.volume = symbol.hechos.body.volume || 0;

      const symbolClone: MarketItem = new MarketItem();
      symbolClone.isTrade = true;

      Object.assign(symbol, symbolClone);
      Object.assign(symbol.hechos.body, messageClone);

      this.emitSymbolChange(message.StockSeries, marketName, symbol);
    }
  }

  private parsePosturas(
    marketType: MarketType,
    message: any,
    topic: any,
  ): void {
    const marketName: string =
      marketType === MarketType.BMV ? 'marketBMV' : 'marketSIC';

    const symbol: MarketItem = this.getMarketElement(
      marketName,
      message.StockSeries,
    );
    if (symbol) {
      const symbolClone: MarketItem = Object.assign({}, symbol);
      Object.assign(symbol.posturas, {
        topic,
        body: this.parsePosturaBody(message),
      });

      symbol.isTrade = false;
      // console.warn('Parse Posturas symbolClone:  %o', symbolClone);
      this.emitSymbolChange(message.StockSeries, marketName, symbol);
    }
  }

  private parseIndexes(message: any): void {
    let SUBTOPIC =
      message.GBL_INDEX !== undefined ? message.GBL_INDEX : message.AME_INDEX;

    SUBTOPIC = SUBTOPIC.toUpperCase();

    const marketName = 'indexes';
    const index: any = {
      name: SUBTOPIC,
      date: message.Timestamp,
      percentageChange: message.ChangePercent,
      price: message.Value,
      volume: message.Volume,
      unitChange: message.ChangeUnit,
      lastValue: message.Value,
      type: message.Type,
      closeValue: message.CloseValue,
    };

    if (this.marketMaps[marketName].has(SUBTOPIC)) {
      const currenData: any = this.getMarketElement(marketName, SUBTOPIC);
      Object.assign(currenData, index);

      if (this.marketObservablesConsumers[marketName].has(SUBTOPIC)) {
        this.emitSymbolChange(
          SUBTOPIC,
          marketName,
          this.getMarketElement(marketName, SUBTOPIC),
        );
      }
    } else {
      this.marketMaps[marketName].set(
        SUBTOPIC,
        this.markets[marketName].push(index),
      );
    }
  }

  private parsePosturaBody(body: any): any {
    const result: any[] = [];

    for (const key in body) {
      if (body.hasOwnProperty(key)) {
        const value: any = body[key];
        let index: any = key.slice(-1);
        let prop: any = key;

        if (!isNaN(index)) {
          index -= 1; // 0 base array
          prop = key.slice(0, key.length - 1);
        } else {
          index = 0;
        }

        if (!result[index]) {
          result[index] = {};
        }

        const obj = result[index];
        obj[prop] = value;
      }
    }

    return result;
  }

  private parseMessageType(json: any) {
    if (json.ElecId) {
      return 'ORD';
    } else if (json.RegType) {
      return json.RegType.trim();
    }
  }

  private parseCommodities(message: any): void {
    const marketName = 'commodities';

    let index: any;
    let SUBTOPIC: any =
      message.METALS !== undefined ? message.METALS : message.AGRIC;

    SUBTOPIC = SUBTOPIC !== undefined ? SUBTOPIC : message.ENERGY;

    SUBTOPIC = SUBTOPIC.toUpperCase();

    index = {
      name: SUBTOPIC,
      date: message.Timestamp,
      percentageChange: message.ChangePercent,
      price: message.Value,
      volume: message.Volume,
      unitChange: message.ChangeUnit,
      lastValue: message.Value,
      type: message.Type,
      closeValue: message.CloseValue,
    };

    if (this.marketMaps[marketName].has(SUBTOPIC)) {
      const currenData: any = this.getMarketElement(marketName, SUBTOPIC);

      Object.assign(currenData, index);

      if (this.marketObservablesConsumers[marketName].has(SUBTOPIC)) {
        this.emitSymbolChange(
          SUBTOPIC,
          marketName,
          this.getMarketElement(marketName, SUBTOPIC),
        );
      }
    } else {
      this.marketMaps[marketName].set(
        SUBTOPIC,
        this.markets[marketName].push(index),
      );
    }
  }

  private parseCurrencies(message: any): void {
    const SUBTOPIC = message.CURRENCY.toUpperCase();
    const marketName = 'currencies';
    const index: any = {
      name: SUBTOPIC,
      date: message.Timestamp,
      percentageChange: message.ChangePercent,
      price: message.Value,
      volume: message.Volume,
      unitChange: message.ChangeUnit,
      lastValue: message.Value,
      type: message.Type,
      closeValue: message.CloseValue,
    };

    if (this.marketMaps[marketName].has(SUBTOPIC)) {
      const currenData: any = this.getMarketElement(marketName, SUBTOPIC);
      Object.assign(currenData, index);

      if (this.marketObservablesConsumers[marketName].has(SUBTOPIC)) {
        this.emitSymbolChange(
          SUBTOPIC,
          marketName,
          this.getMarketElement(marketName, SUBTOPIC),
        );
      }
    } else {
      this.marketMaps[marketName].set(
        SUBTOPIC,
        this.markets[marketName].push(index),
      );
    }
  }

  /**
   * Sets a message in the corresponding market/subtopic.
   * If a consumer is subscribed to that specific element it emits the current value.
   * @param message The message arriving from Solace or MD Endpoints
   */
  private setMessageInSubtopic(message: BaseCommodityAdapter) {
    /**
     * TODO: We must eventually replace all the logic from
     * parseCurrencies, parseCommodities and parseIndexes
     * to use this method instead. In order to properly achieve that we will need
     * to create adapters for information comming from solace and create those instances inside
     * processCallbackStream.
     */
    const { subtopic, marketName } = message;
    const index = message.parse();

    if (this.marketMaps[marketName].has(subtopic)) {
      /**
       * TODO: We should implement a different method to help updating the value from market/subtopic.
       * The following code is the current implementation in other sections of this same file.
       * Here the value stored in the selected market/subtopic is updated by reference but I think is not explicit enough
       * and it might end up to some problems in the future.
       * This should be modified in a different PR to validate all the implications.
       */
      const currenData: any = this.getMarketElement(marketName, subtopic);
      Object.assign(currenData, index);

      this.marketObservablesConsumers[marketName].has(subtopic) &&
        this.emitSymbolChange(
          subtopic,
          marketName,
          this.getMarketElement(marketName, subtopic),
        );
    } else {
      this.marketMaps[marketName].set(
        subtopic,
        this.markets[marketName].push(index),
      );
    }
  }
}
